// Per-item discounts for a MediKredit claim.
//
// A discount is never a claim item. It is submitted as `sub_disc` on the item
// it reduces, with that item's `price`/`gross` left at the normal price (spec
// §4.1) — the scheme then sees "R3 000 product, R1 500 provider discount"
// rather than the same NAPPI code apparently changing price between claims.
// TX-level `sub_disc` is marked pharmacy-only, so a doctor claim has nowhere
// else to declare it.
//
// Shared by SubmitClaim and ReverseClaim so a reversal expresses a discount
// the same way the original submission did.

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * @param {Array} invoiceLines  Every line on the invoice, discounts included.
 * @param {Array} claimLines    The lines that will become <ITEM>s — the caller
 *                              filters these, since submission and reversal
 *                              select differently.
 * @param {Function} valueOfLine  A claim line's medical aid value. Must be the
 *                              same figure the caller sends as `price`, or the
 *                              pro-rata split below will not match the items.
 * @returns {{subDiscForLine: Function, totalDiscount: number, grossFromLines: number}}
 */
export function buildClaimDiscounts(invoiceLines, claimLines, valueOfLine) {
  const lines = Array.isArray(invoiceLines) ? invoiceLines : [];
  const items = Array.isArray(claimLines) ? claimLines : [];

  // Discounts attached to a line (parentUid) already name their item. Whole-
  // invoice ones do not and have to be allocated. Patient-side discounts never
  // reach the claim at all.
  const itemDiscountByUid = new Map();
  let unallocatedMedAidDiscount = 0;
  for (const l of lines) {
    if (l?.type !== "discount") continue;
    if (l.discountTarget !== "medicalAid") continue;
    const amt = Math.abs(Number(l.discountAmount ?? l.rate ?? 0) || 0);
    if (!amt) continue;
    if (l.parentUid) {
      itemDiscountByUid.set(
        l.parentUid,
        round2((itemDiscountByUid.get(l.parentUid) || 0) + amt),
      );
    } else {
      unallocatedMedAidDiscount = round2(unallocatedMedAidDiscount + amt);
    }
  }

  // A whole-invoice medical-aid discount has no single item to sit on, so it
  // is spread across the medical-aid items in proportion to their value. The
  // rounding residual goes on the largest item so the sub_disc total still
  // equals the discount to the cent.
  const itemDiscountByLine = new Map();
  if (unallocatedMedAidDiscount > 0) {
    const medAidLines = items.filter((l) => valueOfLine(l) > 0);
    const medAidGross = round2(
      medAidLines.reduce((sum, l) => sum + valueOfLine(l), 0),
    );
    if (medAidGross > 0) {
      let allocated = 0;
      let largest = medAidLines[0];
      for (const l of medAidLines) {
        const share = round2(
          (valueOfLine(l) / medAidGross) * unallocatedMedAidDiscount,
        );
        itemDiscountByLine.set(l, share);
        allocated = round2(allocated + share);
        if (valueOfLine(l) > valueOfLine(largest)) largest = l;
      }
      const residual = round2(unallocatedMedAidDiscount - allocated);
      if (residual !== 0) {
        itemDiscountByLine.set(
          largest,
          round2((itemDiscountByLine.get(largest) || 0) + residual),
        );
      }
    }
  }

  const subDiscForLine = (l) =>
    round2(
      (l?.uid ? itemDiscountByUid.get(l.uid) || 0 : 0) +
        (itemDiscountByLine.get(l) || 0),
    );

  return {
    subDiscForLine,
    totalDiscount: round2(
      [...itemDiscountByUid.values()].reduce((sum, v) => sum + v, 0) +
        unallocatedMedAidDiscount,
    ),
    grossFromLines: round2(
      items.reduce((sum, l) => sum + valueOfLine(l), 0),
    ),
  };
}
